import type { Dispatch } from 'redux'
import {
  EditorSelection,
  FeedbackType,
  QuickActionPanelType,
} from '@chegg-wt/core-wa/types'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import {
  ProofReaderCardActions,
  SentenceOffset,
  ToastType,
} from '@chegg-wt/core-wa/state/types'
import {
  notifyToast,
  setQuickActionPanelType,
  setRewritePanelSentences,
} from '@chegg-wt/core-wa/state/actions'
import { clearWindowSelection, getWordCount } from '@chegg-wt/core-wa/utils/helpers'
import history from '@chegg-wt/core-wa/utils//history/history'
import {
  HistoryChangeType,
  HistoryInteractionType,
} from '@chegg-wt/core-wa/utils/history/types'
import { getSelectedSentences } from '../../BubbleMenuContainer/utils/getCompleteSelection'
import { actionOriginsSelection, actionOriginsSuggestionClick } from '../constants'

export const onCloseQARightPanel = (dispatch: Dispatch) => {
  const editor = getFeatureEditor()
  editor.setEditable(true)
  dispatch(setRewritePanelSentences([]))
  dispatch(setQuickActionPanelType(null))
  const { from, to } = editor.state.selection
  editor.commands.removeCustomHighlight({
    id: 'feedback.id',
    offset: from,
    length: to - from,
  })
  clearWindowSelection()
}

export const getWordDiffCount = (
  selectedText: string,
  suggestedText: string,
  actionType?: QuickActionPanelType
): number => {
  const selectedWordCount = getWordCount(selectedText)
  const suggestedWordCount = getWordCount(suggestedText)

  if (actionType === QuickActionPanelType.EXPAND)
    return suggestedWordCount - selectedWordCount
  if (actionType === QuickActionPanelType.SHORTEN)
    return selectedWordCount - suggestedWordCount
  return 0
}

export const handleApplySuggestion = (
  selection: EditorSelection,
  dispatch: Dispatch,
  suggestedText: string,
  actionType: QuickActionPanelType
) => {
  const { selectedText, from } = selection
  const editor = getFeatureEditor()

  dispatch(
    userClick({
      origin: actionOriginsSuggestionClick[actionType],
      text: selectedText,
      value: suggestedText,
      citationMetadata: {
        proofReaderCardAction: ProofReaderCardActions.ACCEPT,
        ...(actionType !== QuickActionPanelType.PARAPHRASE && {
          word_count: getWordDiffCount(selectedText, suggestedText, actionType),
        }),
      },
    })
  )

  let historyChangeType: HistoryChangeType = null
  switch (actionType) {
    case QuickActionPanelType.EXPAND:
      historyChangeType = HistoryChangeType.EXPAND
      break
    case QuickActionPanelType.SHORTEN:
      historyChangeType = HistoryChangeType.SHORTEN
      break
    default:
      historyChangeType = HistoryChangeType.PARAPHRASE
  }
  history.addHistoryChangeDetails({
    type: historyChangeType,
    action: HistoryInteractionType.SUGGESTION_ACCEPTED,
  })
  editor
    .chain()
    .replaceCustomUnderlineText(
      {
        id: 'paraphrase', // would work for paraphrase, expand & shorten
        offset: from,
        length: selectedText.length,
        nonManualUpdateType: FeedbackType.REWRITE, // would work for rewrite, expand & shorten
      },
      suggestedText
    )
    .run()

  onCloseQARightPanel(dispatch)
}

export const handleThreeDotsClick = (
  dispatch: Dispatch,
  originalText: string,
  suggestedText: string,
  actionType: QuickActionPanelType
) => {
  dispatch(notifyToast(ToastType.FLAG))
  dispatch(
    userClick({
      origin: actionOriginsSuggestionClick[actionType],
      text: originalText,
      value: suggestedText,
      citationMetadata: { proofReaderCardAction: ProofReaderCardActions.FLAG },
    })
  )
  onCloseQARightPanel(dispatch)
}
export const sendQuickActionSelectedRioCall = (
  dispatch: Dispatch,
  selection: EditorSelection,
  sentenceOffsets: SentenceOffset[],
  quickActionPanelType: QuickActionPanelType
) => {
  const { from, to } = selection
  const selectedSentenceOffsets = getSelectedSentences(sentenceOffsets, from, to)
  const completeSentence = selectedSentenceOffsets
    .map((sentence) => sentence.originalText)
    .join('')
  dispatch(
    userClick({
      origin: actionOriginsSelection[quickActionPanelType],
      text: completeSentence,
    })
  )
}

export const displaySelectedTextIfQAPanelOpen = (
  selection: EditorSelection,
  sentenceOffsets: SentenceOffset[]
) => {
  const { from, to } = selection
  const selectedSentenceOffsets = getSelectedSentences(sentenceOffsets, from, to)

  // expand selection to whole sentences
  const newFrom = selectedSentenceOffsets[0].offset
  const lastSentenceOffSet: SentenceOffset =
    selectedSentenceOffsets.length > 1
      ? selectedSentenceOffsets[selectedSentenceOffsets.length - 1]
      : selectedSentenceOffsets[0]
  const newTo = lastSentenceOffSet.offset + lastSentenceOffSet.length

  const editor = getFeatureEditor()
  editor
    .chain()
    .removeCustomHighlight({
      id: selectedSentenceOffsets[0].id,
      offset: newFrom,
      length: newTo - newFrom,
    })
    .setCustomHighlight({
      id: selectedSentenceOffsets[0].id,
      type: FeedbackType.REWRITE,
      offset: newFrom,
      length: newTo - newFrom,
    })
    .setTextSelection({ from: newFrom, to: newTo })
    .run()

  clearWindowSelection()
}
